import { ArrowUpRight, BarChart3, CalendarClock, CircleDollarSign, Banknote, Users, Receipt, Box, Plus, ChevronRight, AlertTriangle, PackageX, Clock } from "lucide-react";
import { Page, Panel, Table, StatusPill, Badge } from "../lib/ui";
import { money, humanDate, can } from "../lib/api";
import { DailyReportButton } from "./DailyReport";

function Kpi({ label, value, trend, icon: Icon, tone, testid }) {
  return (
    <div className="kpi" data-testid={testid}>
      <div className={`kpi-icon ${tone}`}><Icon size={19} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{trend}</small>
      </div>
    </div>
  );
}

function Alerts({ data, setPage }) {
  if (!data) return null;
  const lowStock = data.low_stock || [];
  const overdueInv = data.overdue_invoices || [];
  const upcomingInv = data.upcoming_invoices || [];
  const overdueChq = data.overdue_cheques || [];
  const upcomingChq = data.upcoming_cheques || [];
  const totalAlerts = lowStock.length + overdueInv.length + upcomingInv.length + overdueChq.length + upcomingChq.length;
  if (totalAlerts === 0) return null;
  return (
    <section className="alerts-panel" data-testid="alerts-panel">
      <div className="alerts-head">
        <div>
          <div className="eyebrow">UYARILAR</div>
          <h2>Dikkat gerektiren {totalAlerts} kayıt</h2>
          <p>Vadesi geçmiş, yaklaşan işlemler ve tükenen stoklar burada.</p>
        </div>
      </div>
      <div className="alerts-grid">
        {overdueInv.length > 0 && (
          <button className="alert-card danger" data-testid="alert-overdue-invoices" onClick={() => setPage("invoices")}>
            <div className="alert-icon"><AlertTriangle size={18} /></div>
            <div className="alert-body">
              <span>Vadesi geçmiş fatura</span>
              <strong>{overdueInv.length}</strong>
              <small>{money(overdueInv.reduce((s, x) => s + (x.total || 0), 0))} toplam tutar</small>
            </div>
            <Badge tone="danger">Gecikmiş</Badge>
          </button>
        )}
        {upcomingInv.length > 0 && (
          <button className="alert-card warn" data-testid="alert-upcoming-invoices" onClick={() => setPage("invoices")}>
            <div className="alert-icon"><Clock size={18} /></div>
            <div className="alert-body">
              <span>Vadesi yaklaşan fatura</span>
              <strong>{upcomingInv.length}</strong>
              <small>Önümüzdeki {data.due_horizon_days || 7} gün içinde</small>
            </div>
            <Badge tone="pending">Yaklaşıyor</Badge>
          </button>
        )}
        {overdueChq.length > 0 && (
          <button className="alert-card danger" data-testid="alert-overdue-cheques" onClick={() => setPage("cheques")}>
            <div className="alert-icon"><AlertTriangle size={18} /></div>
            <div className="alert-body">
              <span>Vadesi geçmiş çek</span>
              <strong>{overdueChq.length}</strong>
              <small>{money(overdueChq.reduce((s, x) => s + (x.amount || 0), 0))} toplam tutar</small>
            </div>
            <Badge tone="danger">Gecikmiş</Badge>
          </button>
        )}
        {upcomingChq.length > 0 && (
          <button className="alert-card warn" data-testid="alert-upcoming-cheques" onClick={() => setPage("cheques")}>
            <div className="alert-icon"><Clock size={18} /></div>
            <div className="alert-body">
              <span>Vadesi yaklaşan çek</span>
              <strong>{upcomingChq.length}</strong>
              <small>Önümüzdeki {data.due_horizon_days || 7} gün içinde</small>
            </div>
            <Badge tone="pending">Yaklaşıyor</Badge>
          </button>
        )}
        {lowStock.length > 0 && (
          <button className="alert-card danger" data-testid="alert-low-stock" onClick={() => setPage("stocks")}>
            <div className="alert-icon"><PackageX size={18} /></div>
            <div className="alert-body">
              <span>Kritik stok</span>
              <strong>{lowStock.length}</strong>
              <small>{data.low_stock_threshold} birim ve altı: {lowStock.slice(0, 3).map((s) => s.code).join(", ")}{lowStock.length > 3 ? "…" : ""}</small>
            </div>
            <Badge tone="danger">Tükeniyor</Badge>
          </button>
        )}
      </div>
    </section>
  );
}

export default function Dashboard({ data, setPage, setModal, user }) {
  const cashflow = data.cashflow || [38, 55, 42, 68, 53, 82];
  const now = new Date();
  const monthLabel = now.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <>
      <Page
        eyebrow={`ÖZET · ${monthLabel.toUpperCase()}`}
        title={`Merhaba, ${user?.name || "Kullanıcı"}.`}
        subtitle="İşletmenizin finansal görünümü bugün böyle."
        action={can(user, "view_daily_report") && <DailyReportButton className="primary-button" />}
      />

      <section className="kpi-grid">
        <Kpi label="Net bakiye" value={money(data.balance)} trend="Bu ay" icon={CircleDollarSign} tone="blue" testid="kpi-balance" />
        <Kpi label="Toplam gelir" value={money(data.income)} trend="Bu ay" icon={ArrowUpRight} tone="green" testid="kpi-income" />
        <Kpi label="Toplam gider" value={money(data.expense)} trend="Bu ay" icon={BarChart3} tone="orange" testid="kpi-expense" />
        <Kpi label="Yaklaşan / bekleyen" value={data.upcoming} trend="Takipte" icon={CalendarClock} tone="slate" testid="kpi-upcoming" />
      </section>

      <div className="dashboard-grid">
        <section className="panel chart-panel">
          <div className="panel-head">
            <div>
              <h2>Nakit akışı</h2>
              <p>Son 6 aylık görünüm</p>
            </div>
            <select data-testid="cashflow-period-select">
              <option>Bu yıl</option>
              <option>Bu ay</option>
            </select>
          </div>
          <div className="chart">
            <div className="chart-y">
              <span>₺100k</span><span>₺75k</span><span>₺50k</span><span>₺25k</span><span>₺0</span>
            </div>
            <div className="bars">
              {cashflow.map((h, i) => (
                <div className="bar-group" key={i}>
                  <div className="bar income" style={{ height: `${h}%` }} />
                  <div className="bar expense" style={{ height: `${Math.max(15, h - 28)}%` }} />
                  <small>{["Oca", "Şub", "Mar", "Nis", "May", "Haz"][i]}</small>
                </div>
              ))}
            </div>
          </div>
          <div className="legend">
            <span><i className="dot blue" /> Gelir</span>
            <span><i className="dot orange" /> Gider</span>
          </div>
        </section>

        <Panel title="Hızlı işlemler" subtitle="Sık kullandığınız alanlar" className="quick-panel">
          {[
            ["invoices", "Fatura oluştur", Receipt, "modal", "invoice"],
            ["stocks", "Stok kartı aç", Box, "modal", "stock"],
            ["customers", "Cari kart ekle", Users, "modal", "customer"],
          ].map(([id, t, Icon, kind, mtype]) => (
            <button
              data-testid={`quick-action-${id}`}
              className="quick-row"
              key={id}
              onClick={() => (kind === "modal" ? setModal({ type: mtype }) : setPage(id))}
            >
              <span className="quick-icon"><Icon size={17} /></span>
              <span>{t}</span>
              <ChevronRight size={16} />
            </button>
          ))}
        </Panel>
      </div>

      <div className="stat-strip">
        <div className="stat-cell">
          <span>Cari hesap</span>
          <strong data-testid="stat-customer-count">{data.customer_count || 0}</strong>
        </div>
        <div className="stat-cell">
          <span>Fatura</span>
          <strong data-testid="stat-invoice-count">{data.invoice_count || 0}</strong>
        </div>
        <div className="stat-cell">
          <span>Banka & Kasa</span>
          <strong data-testid="stat-bank-balance">{money(data.bank_balance || 0)}</strong>
        </div>
      </div>

      <Alerts data={data.alerts} setPage={setPage} />

      <Panel
        title="Son işlemler"
        subtitle="En son eklenen finansal kayıtlar"
        className="recent-panel"
        action={
          <button data-testid="view-all-records-button" className="text-button" onClick={() => setPage("records")}>
            Tümünü gör <ArrowUpRight size={15} />
          </button>
        }
      >
        <Table
          columns={[{ label: "İşlem" }, { label: "Kategori" }, { label: "Tarih" }, { label: "Durum" }, { label: "Tutar", align: "right" }]}
          rows={data.recent || []}
          empty="Henüz kayıt yok. İlk işleminizi ekleyin."
          renderRow={(r) => (
            <tr key={r.id}>
              <td><strong>{r.title}</strong><small>{r.type === "income" ? "Gelir" : "Gider"}</small></td>
              <td>{r.category}</td>
              <td>{r.date}</td>
              <td><StatusPill status={r.status} /></td>
              <td className={`amount ${r.type === "income" ? "positive" : "negative"}`}>
                {r.type === "income" ? "+" : "-"}{money(r.amount)}
              </td>
            </tr>
          )}
        />
      </Panel>
    </>
  );
}
